import { SvgIcon } from '@progress/kendo-react-common';
import { FileSystemExtendedAvailable } from '../../../../../common/helpers';
import {
    DiversionManagerAirlineOptions,
    DiversionManagerCapacityFieldOptions,
    DiversionManagerNetworkOptions,
    DiversionManagerOtherOptions,
    DiversionPriorityTypeOptions,
    LatLongDisplayFormatLogFileOptions
} from '../../../../../components/weather/settings/settings-options';
import {
    AirplaneOutline,
    BorderColorOutline,
    DeltaOutline,
    ExternalLinkOutline,
    FilterOutline,
    InfoCircleOutline,
    NotificationOutline,
    PaletteOutline,
    StatusMonitorOutline,
    ViewGridSolid,
    WarningTriangleOutline,
    WeatherRadarOutline
} from '../../../../../icons/icons';
import { EliType } from '../../../../../services/external-link-interface/EliService';
import {
    AirportIdDisplayStyleOptions,
    AirportMonitorSortOptions,
    DateDelimiterOptions,
    DateFormatOptions,
    DistanceUnitsOptions,
    EliOpenInModeOptions,
    EventListBackgroundColorModeOptions,
    FleetMonitorSortOptions,
    FobVolumeDisplayOptions,
    FobWeightDisplayOptions,
    LatLongDisplayFormatOptions,
    MapGotoTargetOptions,
    MapImageFormatOptions,
    MapLanguageOptions,
    MyFleetAcidListSourceOptions,
    MyFleetModeOptions,
    OnMapControlsDisplayOptions,
    SortDirectionOptions,
    StarmapTypeOptions,
    TimeFormatOptions,
    TimeZoneOptions,
    WeatherLegendDateTimeDisplayOptions
} from '../../../../../types/dropdown-options';
import { commonAircraftThresholdDefinitions, commonAirportThresholdDefinitions } from '../../../../../types/thresholds';
import feVersion from '../../../../../utils/version-helper';
import { MAP_INDEXEDDB_KEY } from '../../../home/layers/MapScreenshot';
import { SETTINGS_ROUTES } from '../../router/settings-routes';
import { SettingsAbout } from '../custom-elements/SettingsAbout';
import { SettingsAlertNotificationsElement } from '../custom-elements/SettingsAlertNotificationsElement';
import { SettingsAlertSoundElement } from '../custom-elements/SettingsAlertSoundElement';
import { SettingsAutoLoginElement } from '../custom-elements/SettingsAutoLoginElement';
import { SettingsAutoScreenshotElement } from '../custom-elements/SettingsAutoScreenshotElement';
import SettingsEtops from '../custom-elements/SettingsEtops';
import { SettingsFullscreenElement } from '../custom-elements/SettingsFullscreenElement';
import { SettingsMapFlashMarkerDuration } from '../custom-elements/SettingsMapFlashMarkerDuration';
import SettingsMapScreenshotSizeElement from '../custom-elements/SettingsMapScreenshotSizeElement';
import { SettingsMapUpdateModeElement } from '../custom-elements/SettingsMapUpdateModeElement';
import { SettingsMyAirportsFilterGroup } from '../custom-elements/SettingsMyAirportsFilterGroup';
import {
    SettingsMyFleetAirlinesElement,
    SettingsMyFleetAirportElement,
    SettingsMyFleetDeskFilterElement
} from '../custom-elements/SettingsMyFleetElement';
import { SettingsTooltipsElement } from '../custom-elements/SettingsTooltipsElement';
import { SettingsViewsSlideShowPeriod } from '../custom-elements/SettingsViewsSlideShowPeriod';
import SettingsWeatherAlwaysLoad from '../custom-elements/SettinsgWeatherAlwaysLoad';
import SettingsAircraftTooltipsGroup from '../custom-groups/SettingsAircraftTooltipsGroup';
import SettingsAirportTooltipsGroup from '../custom-groups/SettingsAirportTooltipsGroup';
import { SettingsEliGroup } from '../custom-groups/SettingsEliGroup';
import SettingsDirectoryPickerElement from './elements/SettingsDirectoryPickerElement';
import SettingsExpandableElement from './elements/SettingsExpandableElement';
import SettingsFilePickerElement from './elements/SettingsFilePickerElement';
import { dropdownElement, SettingsSectionType } from './types';

const aircraftThresholdsSearchTag = Object.values(commonAircraftThresholdDefinitions)
    .map((x) => x.name)
    .join(' ');
const airportThresholdsSearchTag = Object.values(commonAirportThresholdDefinitions)
    .map((x) => x.name)
    .join(' ');

export const generalSection: SettingsSectionType = {
    name: 'General',
    path: 'general',
    icon: PaletteOutline,
    groups: [
        {
            name: 'Appearance',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Use dark mode',
                    value: 'isDark'
                },
                dropdownElement({
                    type: 'dropdown',
                    name: 'On map controls',
                    value: 'OnMapControlsDisplay',
                    options: OnMapControlsDisplayOptions,
                    searchTag: 'map controls display show hide show on hover fullscreen'
                }),
                {
                    type: 'bool',
                    name: 'Show stars in globe view',
                    value: 'hasStars'
                },
                dropdownElement({
                    type: 'dropdown',
                    name: 'Star map type',
                    value: 'starmapType',
                    options: StarmapTypeOptions,
                    searchTag: 'stars milky way sky globe starmap basic enhanced space'
                }),
                {
                    type: 'bool',
                    name: 'Show atmosphere in globe view',
                    value: 'hasAtmosphere'
                }
            ]
        },
        {
            name: 'Fuel',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    name: 'Fuel weight unit',
                    value: 'flightFobWeightDisplayUnit',
                    options: FobWeightDisplayOptions,
                    searchTag: 'FOB fuel weight unit lb lbs kg kilograms pounds'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Fuel volume unit',
                    value: 'flightFobVolumeDisplayUnit',
                    options: FobVolumeDisplayOptions,
                    searchTag: 'FOB fuel volume unit gallon liter litre'
                })
            ]
        },
        {
            name: 'Date/Time',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    options: TimeZoneOptions,
                    name: 'Time zone',
                    value: 'timeDisplayZone',
                    searchTag: 'UTC local timezone offset'
                }),
                dropdownElement({
                    type: 'dropdown',
                    options: TimeFormatOptions,
                    name: 'Time format',
                    value: 'timeDisplayFormat',
                    searchTag: '12 hour 24 hour AM PM clock hhmm'
                }),
                dropdownElement({
                    type: 'dropdown',
                    options: DateFormatOptions,
                    name: 'Date format',
                    value: 'dateDisplayFormat',
                    searchTag: 'MM DD YY YYYY day month year order'
                }),
                dropdownElement({
                    type: 'dropdown',
                    options: DateDelimiterOptions,
                    name: 'Date delimiters',
                    value: 'dateDelimiter',
                    searchTag: 'slash dash dot separator divider'
                }),
                {
                    type: 'bool',
                    name: 'Show day with times (hh:mm/dy)',
                    value: 'showDayWithTimes'
                }
            ]
        },
        {
            name: 'Distance',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    options: DistanceUnitsOptions,
                    name: 'Distance unit',
                    value: 'distanceUnit',
                    searchTag: 'NM nautical miles km kilometers statute'
                })
            ]
        },
        {
            name: 'Latitude/longitude',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    options: LatLongDisplayFormatOptions,
                    name: 'Display format',
                    value: 'latLongDisplayFormat',
                    searchTag: 'DMS decimal degrees minutes seconds coordinates lat long GPS position format'
                })
            ]
        },
        {
            name: 'Recording',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    element: FileSystemExtendedAvailable ? (
                        <></>
                    ) : (
                        <div className="px-3 d-flex gap-1 align-items-center text-warning">
                            <SvgIcon icon={WarningTriangleOutline} />
                            Recording and playback are only support in Chrome based browsers.
                        </div>
                    ),
                    searchTag: 'recording'
                },
                {
                    type: 'bool',
                    name: 'Auto start recording',
                    value: 'autoStartRecording'
                },
                {
                    type: 'bool',
                    name: 'Split recording at midnight',
                    value: 'splitRecordingAtMidnight'
                },
                {
                    type: 'custom',
                    element: <SettingsDirectoryPickerElement indexedDbKey="recording" title="Recording folder" />,
                    searchTag: 'recording directory Record folder'
                }
            ]
        },
        {
            name: 'Map Screenshot',
            type: 'normal',
            elements: [
                {
                    type: 'text-input',
                    name: 'File name',
                    value: 'mapImageFileName'
                },
                dropdownElement({
                    type: 'dropdown',
                    options: MapImageFormatOptions,
                    name: 'Image format',
                    value: 'mapImageFormat',
                    searchTag: 'PNG JPG JPEG WebP screenshot picture export'
                }),
                {
                    type: 'custom',
                    searchTag: 'size width height resolution dimensions image screenshot',
                    element: <SettingsMapScreenshotSizeElement />
                },
                {
                    type: 'bool',
                    name: 'Append date to file name',
                    value: 'appendDateToMapImageFileName'
                },
                {
                    type: 'text-input',
                    name: 'Server URL',
                    hint: 'Optional',
                    placeholder: 'https://example.com',
                    value: 'mapImageServerUrl'
                },
                {
                    type: 'custom',
                    element: <SettingsDirectoryPickerElement title="Default folder" indexedDbKey={MAP_INDEXEDDB_KEY} />
                },
                {
                    type: 'custom',
                    element: <SettingsAutoScreenshotElement />,
                    searchTag: 'Take a screenshot automatically in slide show mode'
                }
            ]
        },
        {
            name: 'Application',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    element: <></>
                },
                {
                    type: 'custom',
                    element: <SettingsFullscreenElement />,
                    searchTag: 'fullscreen full screen startup launch kiosk'
                },
                {
                    type: 'bool',
                    name: 'Auto start slideshow',
                    value: 'autoStartSlideshow',
                    searchTag: 'slideshow auto start presentation views rotate'
                },
                {
                    type: 'bool',
                    name: 'Enable debug features',
                    value: 'enableDebugFeatures',
                    searchTag: 'debug developer diagnostics advanced features'
                },
                {
                    type: 'custom',
                    element: <SettingsMapUpdateModeElement />,
                    searchTag: 'Navigation data'
                },
                {
                    type: 'custom',
                    element: <SettingsAutoLoginElement />,
                    searchTag: 'Automatic login'
                },
                { type: 'link', name: 'Clocks', to: `/settings/${SETTINGS_ROUTES.general.index}/${SETTINGS_ROUTES.general.clocks}` }
            ]
        },
        {
            name: 'Map',
            type: 'normal',
            elements: [
                {
                    type: 'dropdown',
                    name: 'Go-to operation target',
                    options: MapGotoTargetOptions,
                    value: 'mapGotoTargetMode',
                    searchTag: 'map center navigate fly goto operation target aircraft airport'
                },
                {
                    type: 'custom',
                    searchTag: 'Time to show map location marker flash flashing marker duration goto',
                    element: <SettingsMapFlashMarkerDuration />
                },
                {
                    type: 'dropdown',
                    name: 'Map language',
                    options: MapLanguageOptions,
                    value: 'mapLanguage',
                    searchTag: 'map labels text language local country names'
                },
                {
                    type: 'custom',
                    element: <SettingsTooltipsElement />,
                    searchTag: 'Require CTRL to be pressed to open following tooltips'
                }
            ]
        },
        {
            name: 'Toolbar',
            type: 'normal',
            elements: [
                { type: 'bool', name: 'Show constraints', value: 'toolbarShowConstraints' },
                { type: 'bool', name: 'Show monitor', value: 'toolbarShowMonitor' }
            ]
        },
        {
            name: 'Airport tooltip',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    searchTag: 'tooltip tooltips airport metar taf info hover data popup display',
                    element: <SettingsAirportTooltipsGroup />
                }
            ]
        }
    ]
};

export const aircraftSection: SettingsSectionType = {
    name: 'Aircraft',
    path: 'aircraft',
    icon: AirplaneOutline,
    groups: [
        {
            id: 'settings-my-fleet',
            name: 'My fleet',
            type: 'normal',
            elements: [
                {
                    id: 'my-fleet-mode',
                    type: 'dropdown',
                    name: 'Mode',
                    value: 'myFleetMode',
                    options: MyFleetModeOptions
                },
                {
                    type: 'custom',
                    searchTag: 'Airlines airline IATA carrier my fleet operators',
                    element: <SettingsMyFleetAirlinesElement />
                },
                {
                    type: 'bool',
                    name: 'For 3-letter airline IDs, only match flights with a number in the 4th position',
                    value: 'myFleetAirlinesApplyFlightNumberPattern',
                    searchTag: 'flight number 4th position filter pattern'
                },
                {
                    type: 'custom',
                    searchTag: 'airports airport hub base my fleet filter',
                    element: <SettingsMyFleetAirportElement />
                },
                dropdownElement({
                    type: 'dropdown',
                    value: 'myFleetAcidListSource',
                    name: 'Update my fleet list via',
                    options: MyFleetAcidListSourceOptions,
                    searchTag: 'ACID list source sync file auto update registration'
                }),
                {
                    type: 'custom',
                    searchTag: 'update file',
                    element: <SettingsFilePickerElement title="Update file" indexedDbKey="my-fleet-file" />
                },
                {
                    type: 'link',
                    name: 'ACID list',
                    to: `/settings/${SETTINGS_ROUTES.aircraft.index}/${SETTINGS_ROUTES.aircraft.acidList}`,
                    searchTag: 'ACID aircraft ID registration list custom my fleet'
                },
                { type: 'custom', searchTag: 'desk filter dispatcher operator', element: <SettingsMyFleetDeskFilterElement /> }
            ]
        },
        {
            name: 'Tooltips',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    searchTag: 'tooltip tooltips aircraft fields hover tag data popup display label',
                    element: <SettingsAircraftTooltipsGroup />
                }
            ]
        },
        {
            name: 'Selected aircraft',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Show tag',
                    value: 'showHighlightedTag'
                },
                {
                    type: 'bool',
                    name: 'Always show icons for pinned aircraft',
                    hint: 'When enabled, icons for pinned aircraft will be shown even when filtered out by filter.',
                    value: 'showWatchedAircraftIcons'
                }
            ]
        },
        {
            name: 'Route flown',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Draw route flown',
                    value: 'drawRouteFlown'
                },
                {
                    type: 'bool',
                    name: 'Draw points',
                    value: 'routeFlownPoints'
                },
                {
                    type: 'bool',
                    name: 'Draw labels',
                    value: 'routeFlownLabels'
                },
                {
                    type: 'bool',
                    name: 'Colorize',
                    value: 'colorizeRouteFlown'
                },
                {
                    type: 'bool',
                    name: 'Smooth',
                    value: 'smoothenRouteFlown'
                }
            ]
        },
        {
            name: 'Planned route',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Draw planned route (general)',
                    value: 'enablePlannedRoutes'
                },
                {
                    type: 'bool',
                    name: 'Draw points',
                    value: 'plannedRoutePoints'
                },
                {
                    type: 'bool',
                    name: 'Draw labels',
                    value: 'plannedRouteLabels'
                },
                {
                    type: 'bool',
                    name: 'Enable time/distance',
                    value: 'plannedRouteTimeDistance'
                },
                {
                    type: 'bool',
                    name: 'Enable planned route',
                    value: 'drawPlannedRoute'
                },
                {
                    type: 'bool',
                    name: 'Enable ATC route',
                    value: 'drawAtcRoute'
                },
                {
                    type: 'bool',
                    name: 'Enable EDTO/ETOPS',
                    value: 'showEtops'
                },
                {
                    type: 'custom',
                    element: (
                        <SettingsExpandableElement name="EDTO/ETOPS colors">
                            <SettingsEtops />
                        </SettingsExpandableElement>
                    )
                },
                {
                    type: 'bool',
                    name: 'Draw great circle route to destination when no planned or ATC route is available',
                    value: 'drawDirectRouteWhenNoRouteAvailable'
                }
            ]
        },
        {
            name: 'Tools',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Enable Focus',
                    value: 'focusEnabled'
                },
                {
                    type: 'bool',
                    name: 'Enable Profile',
                    value: 'profileEnabled'
                },
                {
                    type: 'bool',
                    name: 'Enable Encompass',
                    value: 'encompassEnabled'
                }
            ]
        },
        {
            name: 'Aircraft type settings',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    element: <></>
                },
                {
                    type: 'link',
                    name: 'Aircraft types parameters',
                    to: `/settings/${SETTINGS_ROUTES.aircraft.index}/${SETTINGS_ROUTES.aircraft.typeParams}`,
                    searchTag: 'aircraft type wake turbulence category performance parameters speed weight'
                },
                {
                    type: 'link',
                    name: 'Aircraft EDR overrides',
                    to: `/settings/${SETTINGS_ROUTES.aircraft.index}/${SETTINGS_ROUTES.aircraft.edrOverrides}`,
                    searchTag: 'EDR energy dissipation rate turbulence override aircraft type custom'
                }
            ]
        }
    ]
};

export const viewsSection: SettingsSectionType = {
    name: 'Views',
    path: 'views',
    icon: ViewGridSolid,
    groups: [
        {
            name: 'User Defined View',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Query to save View if changed',
                    value: 'queryToSaveViewIfChanged'
                },
                {
                    type: 'custom',
                    searchTag: 'Time to show each View in Slideshow mode',
                    element: <SettingsViewsSlideShowPeriod />
                }
            ]
        }
    ]
};

export const weatherSection: SettingsSectionType = {
    name: 'Weather',
    path: 'weather',
    icon: WeatherRadarOutline,
    groups: [
        {
            name: 'Legend',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    name: 'Date/time',
                    value: 'weatherLegendDateTimeDisplayStyle',
                    options: WeatherLegendDateTimeDisplayOptions,
                    searchTag: 'weather legend date time display UTC local format'
                }),
                {
                    type: 'bool',
                    name: 'Display model run time',
                    value: 'weatherLegendDisplayModelRunTime'
                },
                {
                    type: 'custom',
                    searchTag: 'weather products always load recorded auto record replay',
                    element: <SettingsWeatherAlwaysLoad />
                }
            ]
        }
    ]
};

export const filterGroupsSection: SettingsSectionType = {
    name: 'Filter groups',
    path: 'filter-groups',
    icon: FilterOutline,
    groups: [
        {
            name: 'General',
            type: 'normal',
            elements: [
                {
                    type: 'link',
                    name: 'Manage filter groups',
                    to: `/settings/${SETTINGS_ROUTES.filterGroups.index}/${SETTINGS_ROUTES.filterGroups.manage}`,
                    searchTag: 'filter group manage create edit delete aircraft airport'
                }
            ]
        }
    ]
};

export const monitorAlertSection: SettingsSectionType = {
    name: 'Monitor & Alert',
    path: 'monitor-alert',
    icon: StatusMonitorOutline,
    groups: [
        {
            name: 'My Airports',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'My Fleet origins, destinations and destination alternates',
                    value: 'myAirportIncludeMyFleetOrgDestAlt'
                },
                {
                    type: 'bool',
                    name: 'Include My Fleet scheduled flights',
                    value: 'myAirportsIncludeScheduledMyFleet'
                },
                {
                    type: 'custom',
                    searchTag: 'Airport filter group',
                    element: <SettingsMyAirportsFilterGroup />
                }
            ]
        },
        {
            name: 'Event list',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Expanded view',
                    value: 'eventListExpandedView'
                },
                {
                    type: 'bool',
                    name: 'Display monitor status',
                    value: 'eventListDisplayMonitorStatus'
                },
                {
                    type: 'bool',
                    name: 'Display important indicator',
                    value: 'eventListDisplayImportantIndicator'
                },
                {
                    type: 'bool',
                    name: 'Display flight status',
                    value: 'eventListDisplayFlightStatus'
                },
                {
                    type: 'dropdown',
                    name: 'Background color mode',
                    options: EventListBackgroundColorModeOptions,
                    value: 'eventListBackgroundColorMode'
                }
            ]
        },
        {
            name: 'Fleet monitor',
            type: 'normal',
            elements: [
                {
                    type: 'bool',
                    name: 'Show 5th data row',
                    hint: 'Shows additional row with fuel and alternates information',
                    value: 'fleetMonitorShow5thDataRow'
                },
                {
                    type: 'bool',
                    name: 'Show Flight Status color background',
                    value: 'fleetMonitorFlightStatusColorBackground'
                },
                {
                    type: 'bool',
                    name: 'Show scheduled flights',
                    value: 'fleetMonitorShowScheduledFlights'
                },
                {
                    type: 'bool',
                    name: 'Show planned flights',
                    value: 'fleetMonitorShowPlannedFlights'
                },
                {
                    type: 'bool',
                    name: 'Show flight Monitor Status on map',
                    value: 'showAircraftMonitorStatus'
                },
                {
                    type: 'bool',
                    name: 'Hide non-breached thresholds in context menu list',
                    value: 'hideNonBreachedThresholdsFlightContextMenu'
                },
                dropdownElement({
                    type: 'dropdown',
                    name: 'Sort by',
                    options: FleetMonitorSortOptions,
                    value: 'defaultFlightMonitorSortType'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Sort direction',
                    options: SortDirectionOptions,
                    value: 'fleetMonitorSortOrder'
                }),
                {
                    id: 'aircraft-thresholds-link',
                    type: 'link',
                    name: 'Thresholds & alerts',
                    to: `/settings/${SETTINGS_ROUTES.monitorAlert.index}/${SETTINGS_ROUTES.monitorAlert.aircraftThresholds}`,
                    searchTag: aircraftThresholdsSearchTag
                },
                {
                    type: 'link',
                    name: 'Options',
                    to: `/settings/${SETTINGS_ROUTES.monitorAlert.index}/${SETTINGS_ROUTES.monitorAlert.aircraftOptions}`,
                    searchTag: 'fleet monitor options beacon code emergency origin departure minutes display'
                }
            ]
        },
        {
            name: 'Airport monitor',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    name: 'Airport ID display style',
                    options: AirportIdDisplayStyleOptions,
                    value: 'airportIdDisplayStyle',
                    searchTag: 'IATA ICAO airport ID code identifier display'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Sort by',
                    options: AirportMonitorSortOptions,
                    value: 'defaultAirportSortType'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Sort direction',
                    options: SortDirectionOptions,
                    value: 'airportMonitorMonitorSortOrder'
                }),
                {
                    type: 'bool',
                    name: 'Show airport Monitor Status on map',
                    value: 'showAirportMonitorStatus'
                },
                {
                    type: 'bool',
                    name: 'Hide non-breached thresholds in context menu list',
                    value: 'hideNonBreachedThresholdsAirportContextMenu'
                },
                {
                    type: 'link',
                    name: 'Thresholds & alerts',
                    to: `/settings/${SETTINGS_ROUTES.monitorAlert.index}/${SETTINGS_ROUTES.monitorAlert.airportThresholds}`,
                    searchTag: airportThresholdsSearchTag
                },
                {
                    type: 'link',
                    name: 'Options',
                    to: `/settings/${SETTINGS_ROUTES.monitorAlert.index}/${SETTINGS_ROUTES.monitorAlert.airportOptions}`,
                    searchTag: 'airport monitor options TAF window hours origin destination alternate status'
                }
            ]
        }
    ]
};

export const externalLinkIntegrationSection: SettingsSectionType = {
    name: 'External link integration',
    path: 'external-link-integration',
    icon: ExternalLinkOutline,
    groups: [
        {
            name: 'Airplane',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    element: <SettingsEliGroup eliType={EliType.airplane} />
                }
            ]
        },
        {
            name: 'Airport',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    element: <SettingsEliGroup eliType={EliType.airport} />
                }
            ]
        },
        {
            name: 'Toolbar',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    element: <SettingsEliGroup eliType={EliType.topBar} />
                }
            ]
        },
        {
            name: 'General',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    options: EliOpenInModeOptions,
                    name: 'Open in',
                    value: 'eliDisplayMode',
                    searchTag: 'external link integration ELI open tab window iframe mode popup'
                })
            ]
        }
    ]
};

export const notesSection: SettingsSectionType = {
    name: 'My Notes',
    path: 'notes',
    icon: BorderColorOutline,
    groups: [
        {
            name: 'General',
            type: 'normal',
            elements: [
                {
                    type: 'link',
                    name: 'Manage notes',
                    to: `/settings/${SETTINGS_ROUTES.notes.index}/${SETTINGS_ROUTES.notes.manage}`,
                    searchTag: 'notes manage create edit delete'
                }
            ]
        }
    ]
};

export const alertsSection: SettingsSectionType = {
    name: 'Alerts',
    path: 'alerts',
    icon: NotificationOutline,
    groups: [
        {
            name: 'Email',
            type: 'normal',
            elements: [
                {
                    type: 'text-input',
                    name: 'To address',
                    value: 'alertEmailToAddress',
                    placeholder: 'email@example.com'
                },
                {
                    type: 'text-input',
                    name: 'Subject line',
                    placeholder: 'FE Alert',
                    value: 'alertEmailSubject'
                },
                {
                    type: 'link',
                    name: 'Custom emails',
                    to: `/settings/${SETTINGS_ROUTES.alerts.index}/${SETTINGS_ROUTES.alerts.emails}`
                }
            ]
        },
        {
            name: 'Notifications',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    searchTag: 'notification permission browser OS desktop popup alert allow deny',
                    element: <SettingsAlertNotificationsElement />
                }
            ]
        },
        {
            name: 'Logs',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    searchTag: 'logs folder',
                    element: <SettingsDirectoryPickerElement title="Logs folder" indexedDbKey={'logs'} />
                },
                {
                    type: 'bool',
                    name: 'Add space after each comma in the log file',
                    value: 'addSpaceAfterCommaLogFile'
                },
                {
                    type: 'bool',
                    name: 'Add heading line at the beginning of the log file',
                    value: 'addHeadingLogFile'
                },
                dropdownElement({
                    type: 'dropdown',
                    name: 'Time zone',
                    options: TimeZoneOptions,
                    value: 'timeZoneLogFile',
                    searchTag: 'log file UTC local timezone offset'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Time format',
                    options: TimeFormatOptions,
                    value: 'timeFormatLogFile',
                    searchTag: 'log file 12 hour 24 hour AM PM clock'
                }),
                {
                    type: 'bool',
                    name: `Remove 'T' and 'L' prefixes from N-number flights`,
                    value: 'sanitizeTailNumbersLogFile'
                },
                {
                    type: 'bool',
                    name: 'Include scratchpad field in log file for aircraft events',
                    value: 'includeScratchPadLogFile'
                },
                dropdownElement({
                    type: 'dropdown',
                    name: 'Position format',
                    options: LatLongDisplayFormatLogFileOptions,
                    value: 'positionFormatLogFile',
                    searchTag: 'log file position latitude longitude DMS decimal degrees format'
                })
            ]
        },
        {
            name: 'Sound',
            type: 'normal',
            elements: [
                {
                    type: 'custom',
                    searchTag: 'alert sound type audio volume notification chime beep',
                    element: <SettingsAlertSoundElement />
                }
            ]
        }
    ]
};

export const diversionManagerSection: SettingsSectionType = {
    name: 'Diversion manager',
    path: 'diversion-manager',
    icon: DeltaOutline,
    groups: [
        {
            name: 'Terminology',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    name: 'Airline',
                    options: DiversionManagerAirlineOptions,
                    value: 'diversionAirlineTerminology'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Network',
                    options: DiversionManagerNetworkOptions,
                    value: 'diversionNetworkTerminology'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Other',
                    options: DiversionManagerOtherOptions,
                    value: 'diversionOtherTerminology'
                })
            ]
        },
        {
            name: 'Capacity fields',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    name: 'Airline',
                    options: DiversionManagerCapacityFieldOptions,
                    value: 'diversionCapacityFieldAirline'
                }),
                dropdownElement({
                    type: 'dropdown',
                    name: 'Network',
                    options: DiversionManagerCapacityFieldOptions,
                    value: 'diversionCapacityFieldNetwork'
                }),
                dropdownElement({
                    type: 'dropdown',
                    options: DiversionManagerCapacityFieldOptions,
                    name: 'Other',
                    value: 'diversionCapacityFieldOther'
                })
            ]
        },
        {
            name: 'Other',
            type: 'normal',
            elements: [
                dropdownElement({
                    type: 'dropdown',
                    name: 'Recovery priority type',
                    options: DiversionPriorityTypeOptions,
                    value: 'diversionPriorityType'
                })
            ]
        }
    ]
};

export const aboutSection: SettingsSectionType = {
    name: 'About',
    path: 'about',
    icon: InfoCircleOutline,
    groups: [
        {
            name: 'Application info',
            type: 'normal',
            elements: [
                { type: 'text', name: 'Version', hint: feVersion.version },
                {
                    type: 'text',
                    name: 'Build',
                    hint: `${feVersion.ciBuildNumberTemplate} ${feVersion.ciBranchNameTemplate}@${feVersion.ciCommitIdTemplate} (${feVersion.buildNumber})`
                },
                { type: 'custom', searchTag: 'nav', element: <SettingsAbout /> }
            ]
        }
    ]
};

export const sections: SettingsSectionType[] = [
    generalSection,
    aircraftSection,
    viewsSection,
    weatherSection,
    filterGroupsSection,
    monitorAlertSection,
    externalLinkIntegrationSection,
    notesSection,
    alertsSection,
    diversionManagerSection,
    aboutSection
];
